import {
    ViewChild, EventEmitter, Component, ElementRef, Input, AfterViewInit, Output, OnDestroy, Renderer2
} from '@angular/core';
import {CommonModule} from '@angular/common';

import {Router, RouterModule} from '@angular/router';

import {takeUntil} from 'rxjs/operators';
import {PageService} from '../_services/page.service';

import {createPopper, Instance} from "@popperjs/core";

import {IMenuItem, User, Contact} from '@nxt/model-core';
import {IconsComponent} from '../icons/icons.component';
import {OnDestroyPage} from '../_inherited/ondestroy.page';
import {AvatarComponent} from '../avatars/avatar.component';

@Component({
    standalone: true,
    imports: [CommonModule, RouterModule, IconsComponent, AvatarComponent],
    selector: 'pop-button',
    template: `
        <div class="relative inline-flex align-middle w-full cursor-pointer">
            <div
                [class]="btnClass"
                [style.color]="labelColor"
                [style.background-color]="backgroundColor"
                (click)="disabled ? showDisableMsg() : toggleDropdown($event)"
                #btnDropdownRef
            >
                <img *ngIf="imgSrc" [src]="imgSrc" [class]="imgClass"/>
                <span *ngIf="label">{{ label }}</span>
                <avatar *ngIf="avatarUser" [size]="avatarSize" ringSize="0" [user]="avatarUser"></avatar>
                <icon [name]="iconName" [class]="iconClass"></icon>
            </div>
        </div>
        <div [style.z-index]="zIndex" style="position: absolute; min-width: 12rem;" #popoverDropdownRef>
            <div class="bg-white text-base py-2 list-none text-left rounded shadow-lg mb-4 border-dark"
                 style="min-width: 12rem; border-width: 1px; max-height: 250px; overflow-y: auto;"
                 [class]="dropdownPopoverShow ? 'block' : 'hidden'"
            >
                <ng-container *ngFor="let item of items">
                    <ng-container *ngIf="item && !item.hide">
                        <div (click)="nav(item)" [style.color]="item.color"
                             class="cursor-pointer text-sm px-3 m-0 py-1 no-underline bg-transparent text-gray-700 hover:bg-gray-100"
                        >
                            {{ item.label ? item.label : item }}
                            <div class="text-xs font-italic" *ngIf="item.tooltip">{{ item.tooltip }}</div>
                        </div>
                    </ng-container>
                </ng-container>
            </div>
        </div>
    `,
})
export class PopButtonComponent extends OnDestroyPage implements AfterViewInit, OnDestroy {
    @Output() select: EventEmitter<any> = new EventEmitter<any>();
    @Output() click: EventEmitter<any> = new EventEmitter<any>();
    @Input() items: IMenuItem[];
    @Input() label: string;
    @Input() imgSrc: string;
    @Input() imgClass: string;
    @Input() labelColor: string;
    @Input() backgroundColor: string;
    @Input() menuPlacement: any;
    @Input() btnClass: string = 'btn-dark';
    @Input() iconName: string;
    @Input() iconClass: string;
    @Input() avatarSize: '6' | '7' | '8' | '9' | '10' | '11' | '12' | '14' | '16' = '6';
    @Input() avatarUser: User | Contact;
    @Input() disabled: string|boolean;
    @Input() zIndex: number = 9999;

    show: boolean;
    dropdownPopoverShow = false;
    private popperInstance: Instance;
    private isAppendedToBody = false;

    @ViewChild("btnDropdownRef", {static: false}) btnDropdownRef: ElementRef;
    @ViewChild("popoverDropdownRef", {static: false}) popoverDropdownRef: ElementRef;

    constructor(
        private router: Router,
        private pSvc: PageService,
        private eRef: ElementRef,
        private renderer: Renderer2
    ) {
        super();
        this.pSvc.clickEsc$
            .pipe(takeUntil(this.d$))
            .subscribe(
                e => {
                    this.show = false;
                    this.dropdownPopoverShow = false;
                }
            );

        this.pSvc.click$
            .pipe(takeUntil(this.d$))
            .subscribe(
                e => {
                    if (e
                        && e.target
                        && this.eRef?.nativeElement
                        && !this.eRef.nativeElement.contains(e.target)
                        && this.popoverDropdownRef?.nativeElement
                        && !this.popoverDropdownRef.nativeElement.contains(e.target)
                    ) {
                        this.show = false;
                        this.dropdownPopoverShow = false;
                    }
                }
            );
    }

    ngAfterViewInit() {
        if (!this.isAppendedToBody && this.popoverDropdownRef?.nativeElement) {
            if (this.popoverDropdownRef.nativeElement.parentNode !== document.body) {
                this.renderer.appendChild(document.body, this.popoverDropdownRef.nativeElement);
                this.isAppendedToBody = true;
            }
        }

        let rect: any = this.eRef?.nativeElement?.getBoundingClientRect ? this.eRef?.nativeElement?.getBoundingClientRect() : null;
        let placement = 'bottom-start';
        if ((rect.top+rect.height)/window.outerHeight > .5) {
            placement = 'top-start';
        }

        this.pSvc.dimensions$.pipe(takeUntil(this.d$)).subscribe(dimensions => {
            if (this.popperInstance) {
                this.popperInstance.destroy();
            }

            this.popperInstance = createPopper(
                this.btnDropdownRef.nativeElement,
                this.popoverDropdownRef.nativeElement,
                {
                    placement: this.menuPlacement || placement,
                    strategy: 'fixed',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 4],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'viewport',
                            }
                        }
                    ]
                }
            );
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.popperInstance) {
            this.popperInstance.destroy();
            this.popperInstance = null;
        }
        if (this.isAppendedToBody && this.popoverDropdownRef?.nativeElement) {
            const element = this.popoverDropdownRef.nativeElement;
            if (element.parentNode === document.body) {
                this.renderer.removeChild(document.body, element);
            }
            this.isAppendedToBody = false;
        }
    }

    toggleDropdown(event) {
        this.click.emit();
        event.preventDefault();
        if (!this.items?.length) {
            this.dropdownPopoverShow = false;
        } else {
            this.dropdownPopoverShow = !this.dropdownPopoverShow;
            if (this.dropdownPopoverShow && this.popperInstance) {
                this.popperInstance.update();
            }
        }
    }

    async nav(item: IMenuItem) {
        if (item.click) {
            item.click();
        } else if (item.routerLink) {
            await this.router.navigate([item.routerLink]);
        } else if (item.url) {
            window.open(item.url, '_self');
        }
        this.select.emit(item);
        this.dropdownPopoverShow = false;
    }

    showDisableMsg() {
        this.pSvc.notification$.next({
            title: 'Not Allowed',
            message: this.disabled.toString()
        });
    }
}
